import { getFirstValidPrice } from '@bloomays-lib/utils.shared';
import { AllMissionGQLItemResult, IPricing, BusinessType } from '@bloomays-lib/types.shared';
import type { DataGridV2Tone } from '@bloomays-lib/ui.shared/datagrid';

/**
 * Front-end derivations & KPI computations for the Missions dashboard (TEC-3032).
 *
 * All metrics are computed client-side from the missions dataset already loaded
 * for the grid — there is no dedicated backend aggregation endpoint.
 */

/** Estimated worked days per month, used to turn a daily rate into a monthly one. */
export const DJ = 20;

/** Margin thresholds (share of the sale price). */
export const MARGIN_GOOD = 0.2; // ≥ 20% → green
export const MARGIN_LOW = 0.15; // 15–20% → amber, < 15% → red

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** A mission enriched with the values needed by the grid, the KPIs and the filters. */
export type MissionRow = AllMissionGQLItemResult & {
  client: string;
  bloomer: string;
  fournisseur: string;
  /** TJM (€/jour) at the reference date */
  vente: number;
  achat: number;
  /** marge journalière (€/jour) */
  margeDay: number;
  /** marge mensuelle estimée (€/mois) */
  margeMonth: number;
  /** taux de marge (0..1) */
  margePct: number;
  /** CA mensuel estimé (€/mois) */
  caMonth: number;
  /** achat mensuel estimé (€/mois) */
  achatMonth: number;
  begin: Date | null;
  end: Date | null;
  /** nb de jours avant la date de fin (négatif si déjà passée), null si pas de date */
  finInDays: number | null;
  /** Account manager (TAM) lié à la mission — nom affichable, '' si aucun. */
  accountManager: string;
  /** Email du TAM, utilisé comme clé de filtre (null si aucun). */
  accountManagerEmail: string | null;
};

const toDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Whole days between `from` (midnight) and `target` (midnight). */
const daysBetween = (target: Date, from: Date): number => {
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const b = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  return Math.round((a - b) / MS_PER_DAY);
};

/** Build the enriched row used by the dashboard from a raw GraphQL mission. */
export const deriveMissionRow = (mission: AllMissionGQLItemResult, now: Date = new Date()): MissionRow => {
  const price = getFirstValidPrice<IPricing>(mission.pricing || [], now);
  const vente = price?.salePricePerDayWithoutTaxes || 0;
  const achat = price?.purchasePricePerDayWithoutTaxes || 0;
  const margeDay = vente - achat;
  const margePct = vente > 0 ? margeDay / vente : 0;
  const end = toDate(mission.endMission);
  const jobOwner = mission.jobOwner;
  const accountManager = jobOwner ? `${jobOwner.firstname ?? ''} ${jobOwner.lastname ?? ''}`.trim() : '';

  return {
    ...mission,
    client: mission.clientSocietyName?.[0] || '',
    bloomer: mission.bloomerFullName?.[0] || '',
    fournisseur: mission.bloomerSocietyName?.[0] || '',
    vente,
    achat,
    margeDay,
    margeMonth: margeDay * DJ,
    margePct,
    caMonth: vente * DJ,
    achatMonth: achat * DJ,
    begin: toDate(mission.beginMission),
    end,
    finInDays: end ? daysBetween(end, now) : null,
    accountManager,
    accountManagerEmail: jobOwner?.email ?? null,
  };
};

/** Visual tone for a margin ratio, following the agreed thresholds. */
export const marginTone = (margePct: number): DataGridV2Tone => {
  if (margePct >= MARGIN_GOOD) return 'success';
  if (margePct >= MARGIN_LOW) return 'warning';
  return 'danger';
};

// ── Predicates reused by filters, tabs and KPIs ──────────────────────────────

export const isUnderMargin = (row: MissionRow): boolean => row.margePct < MARGIN_LOW;

/** Mission active dont la date de fin tombe dans 0 à 60 jours. */
export const isDueWithin60 = (row: MissionRow): boolean =>
  !!row.isActive && row.finInDays !== null && row.finInDays >= 0 && row.finInDays <= 60;

export const isDueWithin30 = (row: MissionRow): boolean => isDueWithin60(row) && (row.finInDays as number) <= 30;

/** Mission à venir = date de démarrage strictement après le jour courant. */
export const isUpcoming = (row: MissionRow, now: Date = new Date()): boolean =>
  !!row.begin && daysBetween(row.begin, now) > 0;

/**
 * Départ le mois prochain = date de fin de mission tombant sur le mois civil M+1
 * (interprétation métier retenue : « départ » = fin/sortie de mission).
 */
export const isLeavingNextMonth = (row: MissionRow, now: Date = new Date()): boolean => {
  if (!row.end) return false;
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return row.end.getFullYear() === next.getFullYear() && row.end.getMonth() === next.getMonth();
};

// ── KPI aggregation ──────────────────────────────────────────────────────────

export type MissionKpis = {
  total: number;
  activeCount: number;
  terminatedCount: number;
  underMarginCount: number;
  margeMonthTotal: number;
  tauxMargeMoyen: number; // 0..1
  caMonthTotal: number;
  achatMonthTotal: number;
  tjmVenteMoyen: number;
  tjmAchatMoyen: number;
  dueWithin60Count: number;
  dueWithin30Count: number;
};

const avg = (values: number[]): number =>
  values.length === 0 ? 0 : values.reduce((sum, v) => sum + v, 0) / values.length;

const sum = (values: number[]): number => values.reduce((s, v) => s + v, 0);

/**
 * Compute the 4 dashboard KPIs over the provided set. The set is expected to be
 * the dataset filtered by the *dimensional* filters (Type / Marge) but across all
 * statuses, since the KPIs themselves break missions down by status (active /
 * terminée / échéances). Financial KPIs are computed over active missions only.
 */
export const computeMissionKpis = (rows: MissionRow[]): MissionKpis => {
  const active = rows.filter((r) => r.isActive);

  return {
    total: rows.length,
    activeCount: active.length,
    terminatedCount: rows.length - active.length,
    underMarginCount: active.filter(isUnderMargin).length,
    margeMonthTotal: sum(active.map((r) => r.margeMonth)),
    tauxMargeMoyen: avg(active.filter((r) => r.vente > 0).map((r) => r.margePct)),
    caMonthTotal: sum(active.map((r) => r.caMonth)),
    achatMonthTotal: sum(active.map((r) => r.achatMonth)),
    tjmVenteMoyen: avg(active.filter((r) => r.vente > 0).map((r) => r.vente)),
    tjmAchatMoyen: avg(active.filter((r) => r.achat > 0).map((r) => r.achat)),
    dueWithin60Count: active.filter(isDueWithin60).length,
    dueWithin30Count: active.filter(isDueWithin30).length,
  };
};

// ── Filter option types (UI segmented controls) ──────────────────────────────

export type StatutFilter = 'actives' | 'toutes' | 'terminees' | 'a_venir';
export type TypeFilter = 'tous' | BusinessType.IT | BusinessType.People;
export type MargeFilter = 'toutes' | 'sous15';
export type MissionTab = 'toutes' | 'echeances' | 'departs';

/** Valeur du select TAM : 'tous' (aucun filtre) ou l'email d'un account manager. */
export type TamFilter = 'tous' | string;

export type TamOption = { value: string; label: string };

/**
 * Liste dédupliquée des TAM (account managers) présents sur les missions,
 * triée par nom — alimente le select du filtre « Account manager ».
 */
export const buildTamOptions = (rows: MissionRow[]): TamOption[] => {
  const byEmail = new Map<string, string>();
  rows.forEach((row) => {
    if (row.accountManagerEmail) {
      byEmail.set(row.accountManagerEmail, row.accountManager || row.accountManagerEmail);
    }
  });
  return [...byEmail.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
};
